import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
import { LayoutTypes } from '~commons/settings/constants/layout-options';
import { VerifiedStyleOptions } from '~commons/settings/constants/verified-style-options';
import { Sorting } from '~settings-commons/constants/sorting';

export type SettingsParamsTypes = {
  headerTitleHtmlTag: SettingsParamType.Text;
  defaultSorting: SettingsParamType.Text;
  reviewCount: SettingsParamType.Number;
  isHeaderTitleEnabled: SettingsParamType.Boolean;
  isReviewBarchartEnabled: SettingsParamType.Boolean;
  isReviewDateEnabled: SettingsParamType.Boolean;
  isFilterEnabled: SettingsParamType.Boolean;
  isSortEnabled: SettingsParamType.Boolean;
  isCardShadowEnabled: SettingsParamType.Boolean;
  layoutType: SettingsParamType.Text;
  verifiedStyleType: SettingsParamType.Text;
};

export type SettingsParams = typeof settingsParams;

const settingsParams = createSettingsParams<SettingsParamsTypes>({
  headerTitleHtmlTag: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'h2',
  },
  isFilterEnabled: { type: SettingsParamType.Boolean, getDefaultValue: () => true },
  isSortEnabled: { type: SettingsParamType.Boolean, getDefaultValue: () => true },
  reviewCount: {
    type: SettingsParamType.Number,
    getDefaultValue: () => 5,
  },
  isHeaderTitleEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  isReviewBarchartEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  defaultSorting: {
    type: SettingsParamType.Text,
    getDefaultValue: () => Sorting.MostRelevant,
    metadata: {
      type: DataType.textEnum,
      defaultValue: { type: 'static', value: Sorting.MostRelevant },
      ai: {
        description: `
        The default order in which the reviews are sorted.
        Possible values:
        - ${Sorting.MostRelevant} Reviews ordered by overall relevance.
        - ${Sorting.Newest} Most recently submitted reviews first.
        - ${Sorting.TopRated} Highest rated reviews first.
        - ${Sorting.MostHelpful} Reviews with the most helpful votes first.
        `,
      },
    },
  },
  isReviewDateEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  isCardShadowEnabled: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
  },
  layoutType: {
    type: SettingsParamType.Text,
    getDefaultValue: () => LayoutTypes.List,
    metadata: {
      type: DataType.textEnum,
      defaultValue: { type: 'static', value: LayoutTypes.List },
      ai: {
        description: `
        The layout used to display the list of reviews.
        Possible values:
        - ${LayoutTypes.List} Reviews shown in a single vertical list.
        - ${LayoutTypes.BigCards} Reviews shown as large cards.
        - ${LayoutTypes.SmallCards} Reviews shown as small, compact cards.
        `,
      },
    },
  },
  verifiedStyleType: {
    type: SettingsParamType.Text,
    getDefaultValue: () => VerifiedStyleOptions.Badge,
    metadata: {
      type: DataType.textEnum,
      defaultValue: { type: 'static', value: VerifiedStyleOptions.Badge },
      ai: {
        description: `
        How the "verified" indicator is displayed on a review.
        Possible values:
        - ${VerifiedStyleOptions.Badge} Shown as a badge.
        - ${VerifiedStyleOptions.Text} Shown as plain text.
        `,
      },
    },
  },
});

export default settingsParams;
